/** Sign-in and agencies: the endpoints the first screens need (the rest load with their screens, see lazy-api.ts) */
import type { ApiClient } from './client';
import type {
  AddMemberRequest,
  Agency,
  AgencyDashboardData,
  CreateInviteRequest,
  Invite,
  SubaccountProfile,
  TeamMemberSummary,
  UpdateAgencyRequest,
  UpdateSubaccountProfileRequest,
  CreateAgencyRequest,
  CreateSubaccountRequest,
  LoginRequest,
  LoginResponse,
  Member,
  RegisteredUser,
  RegisterRequest,
  RegisterResponse,
  RemoveMemberResponse,
  Subaccount,
  UpdateMemberRequest,
  UpdateSubaccountRequest,
  LoginUser,
} from './types';

export type { BookingApi, CalendarApi } from './booking';
export type { InvitesApi, SecurityApi } from './security';
export type { PipelinesApi } from './pipelines';

export type LoginResult = { status: 'signed_in'; user: LoginUser } | { status: 'mfa_required' };

const enc = encodeURIComponent;

export function authApi(client: ApiClient) {
  return {
    /** POST /auth/login. Installs the session unless the account needs an MFA code. */
    async login(body: LoginRequest): Promise<LoginResult> {
      const data = await client.request<LoginResponse>('POST', '/auth/login', { body, auth: false });
      if ('requiresMFA' in data) return { status: 'mfa_required' };
      client.setSession(data.tokens);
      return { status: 'signed_in', user: data.user };
    },

    /** POST /auth/register. The backend provisions a workspace tenant and signs the user in. */
    async register(body: RegisterRequest): Promise<RegisteredUser> {
      const data = await client.request<RegisterResponse>('POST', '/auth/register', { body, auth: false });
      client.setSession(data.tokens);
      return data.user;
    },

    logout: () => client.logout(),

    /** POST /auth/password/forgot. Always succeeds (the API never says whether the email exists). */
    forgotPassword: (email: string) =>
      client.request<{ requested: true }>('POST', '/auth/password/forgot', { body: { email }, auth: false }),
    /** POST /auth/password/reset. Ends every session of the account. */
    resetPassword: (token: string, newPassword: string) =>
      client.request<{ passwordReset: true }>('POST', '/auth/password/reset', { body: { token, newPassword }, auth: false }),
  };
}

export function agenciesApi(client: ApiClient) {
  return {
    list: () => client.request<Agency[]>('GET', '/agencies'),
    get: (agencyId: string) => client.request<Agency>('GET', `/agencies/${enc(agencyId)}`),
    create: (body: CreateAgencyRequest) => client.request<Agency>('POST', '/agencies', { body }),

    listSubaccounts: (agencyId: string, options: { includeArchived?: boolean } = {}) =>
      client.request<Subaccount[]>('GET', `/agencies/${enc(agencyId)}/subaccounts`, {
        query: { includeArchived: options.includeArchived ? 'true' : undefined },
      }),
    createSubaccount: (agencyId: string, body: CreateSubaccountRequest) =>
      client.request<Subaccount>('POST', `/agencies/${enc(agencyId)}/subaccounts`, { body }),
    updateSubaccount: (agencyId: string, subaccountId: string, body: UpdateSubaccountRequest) =>
      client.request<Subaccount>('PATCH', `/agencies/${enc(agencyId)}/subaccounts/${enc(subaccountId)}`, { body }),

    listMembers: (agencyId: string) => client.request<Member[]>('GET', `/agencies/${enc(agencyId)}/members`),
    addMember: (agencyId: string, body: AddMemberRequest) =>
      client.request<Member>('POST', `/agencies/${enc(agencyId)}/members`, { body }),
    updateMember: (agencyId: string, userId: string, body: UpdateMemberRequest) =>
      client.request<Member>('PATCH', `/agencies/${enc(agencyId)}/members/${enc(userId)}`, { body }),
    removeMember: (agencyId: string, userId: string) =>
      client.request<RemoveMemberResponse>('DELETE', `/agencies/${enc(agencyId)}/members/${enc(userId)}`),
    /** The team as staff see it (userId, email, role). */
    listTeam: (agencyId: string) => client.request<TeamMemberSummary[]>('GET', `/agencies/${enc(agencyId)}/members`),

    update: (agencyId: string, body: UpdateAgencyRequest) => client.request<Agency>('PATCH', `/agencies/${enc(agencyId)}`, { body }),
    dashboard: (agencyId: string) => client.request<AgencyDashboardData>('GET', `/agencies/${enc(agencyId)}/dashboard`),
    getProfile: (agencyId: string, subaccountId: string) =>
      client.request<SubaccountProfile>('GET', `/agencies/${enc(agencyId)}/subaccounts/${enc(subaccountId)}/profile`),
    updateProfile: (agencyId: string, subaccountId: string, body: UpdateSubaccountProfileRequest) =>
      client.request<SubaccountProfile>('PUT', `/agencies/${enc(agencyId)}/subaccounts/${enc(subaccountId)}/profile`, { body }),

    listInvites: (agencyId: string) => client.request<Invite[]>('GET', `/agencies/${enc(agencyId)}/invites`),
    createInvite: (agencyId: string, body: CreateInviteRequest) =>
      client.request<Invite>('POST', `/agencies/${enc(agencyId)}/invites`, { body }),
    resendInvite: (agencyId: string, inviteId: string) =>
      client.request<Invite>('POST', `/agencies/${enc(agencyId)}/invites/${enc(inviteId)}/resend`),
    revokeInvite: (agencyId: string, inviteId: string) =>
      client.request<Invite>('DELETE', `/agencies/${enc(agencyId)}/invites/${enc(inviteId)}`),
  };
}

export type AuthApi = ReturnType<typeof authApi>;
export type AgenciesApi = ReturnType<typeof agenciesApi>;
